import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Database, Rss, ShoppingBag, LogOut, Search,
  PanelLeftClose, PanelLeftOpen, SlidersHorizontal, Sparkles, Activity, FolderTree,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import CommandPalette from './CommandPalette';
import FeedyBattle from './FeedyBattle';
import RunPipelineButton from './RunPipelineButton';
import { CHANGELOG } from '../data/changelog';
import { cn } from '../lib/utils';
import { api } from '../api/feedy';
import usePollWhileActive from '../hooks/usePollWhileActive';

const rand = (min, max) => min + Math.random() * (max - min);

const NEWS_DOT = { performance: 'bg-emerald-500', feature: 'bg-sky-500', reliability: 'bg-amber-500', fix: 'bg-rose-500' };

// Top-right "What's new" menu — surfaces the latest changelog entries from
// anywhere, with an unread dot until the newest entry has been opened.
function NewsMenu({ onViewAll }) {
  const latest = CHANGELOG[0]?.date;
  const [seen, setSeen] = useState(() => { try { return localStorage.getItem('feedy_news_seen'); } catch { return null; } });
  const unread = latest && seen !== latest;
  const markSeen = () => { try { localStorage.setItem('feedy_news_seen', latest); } catch { /* ignore */ } setSeen(latest); };
  return (
    <DropdownMenu onOpenChange={(o) => { if (o && unread) markSeen(); }}>
      <DropdownMenuTrigger asChild>
        <button className="relative grid h-9 w-9 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" title="What's new" aria-label="What's new">
          <Sparkles className="h-4 w-4" />
          {unread && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-primary" /> What&apos;s new</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto py-1">
          {CHANGELOG.slice(0, 4).map((e, i) => (
            <div key={i} className="px-2 py-1.5">
              <div className="mb-0.5 flex items-center gap-2">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${NEWS_DOT[e.tag] || 'bg-muted-foreground'}`} />
                <span className="text-[12px] font-medium">{e.title}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground tnum">{e.date}</span>
              </div>
              <ul className="space-y-0.5 pl-3.5">
                {e.items.slice(0, 2).map((it, j) => <li key={j} className="text-[11px] leading-snug text-muted-foreground">{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onViewAll} className="justify-center text-[12px] text-muted-foreground">View all on dashboard</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Grouped by pipeline stage so the nav mirrors the data flow. The first group
// has no header (primary destinations); the rest carry a muted section label.
// "Runs" is the pipeline-run history (the /schedule route — the cron lives in
// Coolify, so this page only shows/triggers runs, hence the honest name).
const NAV = [
  { items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/feeds', label: 'Feeds', icon: Rss },
  ] },
  { section: 'Catalog', items: [
    { to: '/sources', label: 'Sources', icon: Database },
    { to: '/master-rules', label: 'Master rules', icon: SlidersHorizontal },
    { to: '/categorisation', label: 'Categorisation', icon: FolderTree },
  ] },
  { section: 'Config', items: [
    { to: '/channels', label: 'Channels', icon: ShoppingBag },
    { to: '/schedule', label: 'Runs', icon: Activity },
  ] },
];

// Header "Run pipeline" control. Tracks the latest run's status so the button
// reflects an in-progress run (disabled + spinner) and live-polls while running.
function HeaderRun() {
  const [running, setRunning] = useState(false);
  const refresh = () => api.pipelineRuns.list()
    .then((runs) => setRunning(runs[0]?.status === 'running'))
    .catch(() => {});
  useEffect(() => { refresh(); }, []);
  usePollWhileActive(running, refresh);
  return <RunPipelineButton running={running} onStarted={() => { setRunning(true); refresh(); }} />;
}

export default function AppLayout({ title, description, actions, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Easter egg: Feedy occasionally marches in and blasts the Channable cube.
  const [battle, setBattle] = useState(false);
  const battleTimer = useRef();
  const clickTimes = useRef([]);
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  useEffect(() => {
    if (reducedMotion) return undefined;
    battleTimer.current = setTimeout(() => setBattle(true), rand(15000, 30000)); // first show
    return () => clearTimeout(battleTimer.current);
  }, [reducedMotion]);

  const endBattle = useCallback(() => {
    setBattle(false);
    if (!reducedMotion) battleTimer.current = setTimeout(() => setBattle(true), rand(240000, 540000)); // 4–9 min
  }, [reducedMotion]);

  // triple-click the brand logo to summon Feedy on demand
  const onBrandClick = () => {
    const now = Date.now();
    clickTimes.current = [...clickTimes.current, now].filter((t) => now - t < 600);
    if (clickTimes.current.length >= 3) { clickTimes.current = []; setBattle(true); }
    else navigate('/dashboard');
  };

  // ⌘K / Ctrl+K opens the command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const initials = (name) => (name ? name.slice(0, 2).toUpperCase() : 'U');

  return (
    <div className="flex min-h-screen">
      <aside
        className={cn(
          'sticky top-0 flex h-screen flex-col justify-between border-r border-border bg-card/70 backdrop-blur transition-all duration-200',
          collapsed ? 'w-[60px]' : 'w-[228px]'
        )}
      >
        <div className="px-3 py-4">
          {/* brand */}
          <button onClick={onBrandClick} title="feedy" className="flex h-9 w-full items-center gap-2.5 px-1.5">
            <span className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground shadow-glow">
              <span className="-mt-0.5 text-base font-bold leading-none">◣</span>
            </span>
            {!collapsed && <span className="font-mono text-[15px] font-semibold tracking-tight">feedy</span>}
          </button>

          {/* command trigger */}
          <button
            onClick={() => setPaletteOpen(true)}
            className={cn(
              'mt-4 flex h-9 w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted',
              collapsed && 'justify-center px-0'
            )}
          >
            <Search className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Search…</span>
                <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
              </>
            )}
          </button>

          <nav className="mt-5">
            {NAV.map((group, gi) => (
              <div
                key={group.section || 'primary'}
                className={cn(
                  gi > 0 && (collapsed ? 'mt-2 border-t border-border/60 pt-2' : 'mt-4')
                )}
              >
                {!collapsed && group.section && (
                  <p className="mb-1 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{group.section}</p>
                )}
                <div className="space-y-0.5">
                  {group.items.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      title={label}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary/12 text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          collapsed && 'justify-center px-0'
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary" />}
                          <Icon className="h-[18px] w-[18px] shrink-0" />
                          {!collapsed && label}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        <div className="px-3 pb-4">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={cn('mb-2 flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted', collapsed && 'justify-center px-0')}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn('flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-muted', collapsed && 'justify-center px-0')}>
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
                  <AvatarFallback className="text-[11px]">{initials(user?.displayName)}</AvatarFallback>
                </Avatar>
                {!collapsed && <span className="truncate text-sm">{user?.displayName || 'Account'}</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="truncate">{user?.displayName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logout()} className="gap-2">
                <LogOut className="h-4 w-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-border bg-background/80 px-6 py-3.5 backdrop-blur-md">
          <div className="min-w-0">
            <h1 className="truncate text-[17px] font-semibold tracking-tight">{title}</h1>
            {description && <p className="truncate text-[13px] text-muted-foreground">{description}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <HeaderRun />
            <NewsMenu onViewAll={() => navigate('/dashboard')} />
            {actions}
          </div>
        </header>
        <div className="flex-1 px-6 py-6">{children}</div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {battle && <FeedyBattle onDone={endBattle} />}
    </div>
  );
}
